import { Suspense, useEffect } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { Outlet, RouteObject } from 'react-router-dom'
import { Scope } from '~api/user/roles/scope-types'
import { GuidedTour } from '~components/guided-tour/guided-tour'
import LoadComponent from '~components/molecules/load-component/load-component'
import { PageGuard } from '~components/page-guard'
import { TermsOfUseModal } from '~components/terms-of-use-modal'
import { WelcomePopup } from '~components/welcome-popup/welcome-popup'
import { ActivePoolProvider } from '~context/active-pool-context'
import { Auth0ProviderWithConfig } from '../components/auth0-provider-with-config'
import { AuthenticationGuard } from '../components/authentication-guard'
import NavigationAndFooter from '../components/navigation-and-footer/navigation-and-footer'
import ErrorPage from '../pages/error-page'
import { openPages } from './open-pages'
import { routesBehindLogin } from './routes-behind-login/routes-behind-login'

interface RouteHandle {
  restrictedTo?: Scope
  withPools?: boolean
  disableLeftNavigation?: boolean
}

export type ExtendedRouteObject = RouteObject & {
  handle: RouteHandle
}

export const pagesBehindLogin = [
  {
    element: (
      <AuthenticationGuard>
        <PageGuard>
          <Outlet />
        </PageGuard>
      </AuthenticationGuard>
    ),
    children: [...routesBehindLogin],
  },
]

const FaviconWrapper = ({ children }: { children: React.ReactNode }) => {
  const host = window.location.host

  useEffect(() => {
    const setFavicon = () => {
      const favicon = document.getElementById('favicon') as HTMLAnchorElement

      if (host.includes('localhost') || host.includes('test')) {
        favicon.href = '/favicon-test.svg'
      }
      if (host.includes('stage')) {
        favicon.href = '/favicon-stage.svg'
      }
    }
    setFavicon()
  }, [host])

  return <>{children}</>
}

export const routeWrapper: RouteObject[] = [
  {
    element: (
      <FaviconWrapper>
        <Auth0ProviderWithConfig>
          <HelmetProvider>
            <ActivePoolProvider>
              <GuidedTour />
              <TermsOfUseModal />
              <WelcomePopup />
              <NavigationAndFooter>
                <Suspense fallback={<LoadComponent />}>
                  <Outlet />
                </Suspense>
              </NavigationAndFooter>
            </ActivePoolProvider>
          </HelmetProvider>
        </Auth0ProviderWithConfig>
      </FaviconWrapper>
    ),
    errorElement: <ErrorPage />,
    children: [...openPages, ...pagesBehindLogin],
  },
]
